import type { JSX } from 'react';
import type { ClientResource, EnvironmentValue } from '@voxler/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Ellipsis, FileSearch, Gift } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  daysInMonth,
  formatDateTimeShort,
  formatInt,
  parisDayKey,
} from '@/components/requests/request-format';
import { PageChrome } from '@/components/page-chrome';
import { useToast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Card, CardBar, CardContent, CardHead } from '@/components/ui/card';
import { ChartLegend, TimeChart, type BarSeries, type LegendItem } from '@/components/ui/chart';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchInput } from '@/components/ui/field';
import { Row4 } from '@/components/ui/layout-grid';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { NativeSelect } from '@/components/ui/native-select';
import { MonthSwitcher, PageHead, formatMonthLabel } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import { StatCard } from '@/components/ui/stat-card';
import { outcomeLabel, requestStatusLabel, StatusBadge } from '@/components/ui/status-tag';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
  rowLinkMonoClass,
} from '@/components/ui/table';
import { TableTabs, type TableTabItem } from '@/components/ui/tabs';
import { internalFetch, internalSend } from '@/lib/api';
import { currentMonth, formatEur, formatEurCents, formatPercent, sumEuros } from '@/lib/format';

/** Row of `GET /internal/requests` (dashboard-internal route, no shared contract). */
interface RequestRow {
  id: string;
  clientId: string;
  useCaseId: string;
  environment: EnvironmentValue;
  status: string;
  outcome: string | null;
  billable: boolean;
  amount: string;
  providerCost: string;
  reference: string | null;
  adjusted: boolean;
  createdAt: string;
}

type Filter = 'all' | 'succeeded' | 'failed' | 'rejected' | 'running';

const FILTERS: readonly { key: Filter; label: string }[] = [
  { key: 'all', label: 'Toutes' },
  { key: 'succeeded', label: 'Réussies' },
  { key: 'failed', label: 'Échouées' },
  { key: 'rejected', label: 'Rejetées' },
  { key: 'running', label: 'En cours' },
];

const EMPTY_MESSAGES: Record<Filter, { title: string; description: string }> = {
  all: {
    title: 'Aucune requête',
    description: 'Aucune requête ne correspond à ce mois et à ces filtres.',
  },
  succeeded: {
    title: 'Aucune requête réussie',
    description: 'Aucune requête réussie ne correspond à ces filtres.',
  },
  failed: {
    title: 'Aucune requête échouée',
    description: 'Aucune requête échouée ne correspond à ces filtres.',
  },
  rejected: {
    title: 'Aucune requête rejetée',
    description: 'Aucune requête rejetée ne correspond à ces filtres.',
  },
  running: {
    title: 'Aucune requête en cours',
    description: "Les requêtes en cours d'exécution apparaissent ici jusqu'à leur issue.",
  },
};

const CHART_LEGEND: readonly LegendItem[] = [
  { label: 'Réussies', color: 'chart-1' },
  { label: 'Échouées', color: 'chart-4' },
  { label: 'Rejetées', color: 'chart-5' },
];

const COLUMN_COUNT = 9;

function canOffer(row: RequestRow): boolean {
  return row.billable && !row.adjusted && row.status === 'succeeded';
}

function billableLabel(row: RequestRow): string {
  if (row.adjusted) {
    return 'Offert à 100 %';
  }
  if (row.environment === 'staging') {
    return 'Non (staging)';
  }
  return row.billable ? 'Oui' : 'Non';
}

/** Daily request counts of the month, per status (Europe/Paris days). */
function dailySeries(
  rows: readonly RequestRow[],
  month: string,
): {
  categories: string[];
  bars: BarSeries[];
} {
  const days = daysInMonth(month);
  const succeeded = new Array<number>(days).fill(0);
  const failed = new Array<number>(days).fill(0);
  const rejected = new Array<number>(days).fill(0);
  for (const row of rows) {
    const key = parisDayKey(row.createdAt);
    if (key === null || !key.startsWith(`${month}-`)) {
      continue;
    }
    const index = Number(key.slice(8, 10)) - 1;
    const target =
      row.status === 'succeeded'
        ? succeeded
        : row.status === 'failed'
          ? failed
          : row.status === 'rejected'
            ? rejected
            : null;
    if (target !== null && index >= 0 && index < days) {
      target[index] = (target[index] ?? 0) + 1;
    }
  }
  return {
    categories: Array.from({ length: days }, (_, index) => String(index + 1)),
    bars: [
      { key: 'succeeded', label: 'Réussies', color: 'chart-1', values: succeeded },
      { key: 'failed', label: 'Échouées', color: 'chart-4', values: failed },
      { key: 'rejected', label: 'Rejetées', color: 'chart-5', values: rejected },
    ],
  };
}

export function RequestsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [filter, setFilter] = useState<Filter>('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [pick, setPick] = useState<RequestRow | null>(null);

  const requests = useQuery({
    queryKey: ['requests', month],
    queryFn: () => internalFetch<{ requests: RequestRow[] }>(`/internal/requests?month=${month}`),
  });
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => internalFetch<{ clients: ClientResource[] }>('/internal/clients'),
  });
  const clientOptions = [...(clients.data?.clients ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name, 'fr'),
  );
  const clientName = (id: string): string =>
    clientOptions.find((client) => client.id === id)?.name ?? id;

  const adjust = useMutation({
    mutationFn: (row: RequestRow) =>
      internalSend('POST', '/internal/adjustments', {
        clientId: row.clientId,
        percent: 100,
        reason: 'Geste commercial',
        requestIds: [row.id],
      }),
    onSuccess: () => {
      setPick(null);
      void queryClient.invalidateQueries({ queryKey: ['requests', month] });
      notify('Requête offerte à 100 %');
    },
  });

  const all = useMemo(() => requests.data?.requests ?? [], [requests.data]);
  const clientScoped = useMemo(
    () => all.filter((row) => clientFilter === 'all' || row.clientId === clientFilter),
    [all, clientFilter],
  );
  const needle = search.trim().toLowerCase();
  const searched = useMemo(
    () =>
      needle.length === 0
        ? clientScoped
        : clientScoped.filter(
            (row) =>
              (row.reference ?? '').toLowerCase().includes(needle) ||
              row.id.toLowerCase().includes(needle),
          ),
    [clientScoped, needle],
  );
  const count = (key: Filter): number =>
    key === 'all' ? searched.length : searched.filter((row) => row.status === key).length;
  const rows = useMemo(
    () => searched.filter((row) => filter === 'all' || row.status === filter),
    [searched, filter],
  );
  const pagination = usePagination(rows, 10);

  const tabs: TableTabItem<Filter>[] = FILTERS.map((item) => ({
    value: item.key,
    label: item.label,
    count: requests.data !== undefined ? count(item.key) : undefined,
  }));

  // Indicators of the month for the selected client (or every client).
  const total = clientScoped.length;
  const succeededCount = clientScoped.filter((row) => row.status === 'succeeded').length;
  const failedCount = clientScoped.filter((row) => row.status === 'failed').length;
  const rejectedCount = clientScoped.filter((row) => row.status === 'rejected').length;
  const billed = sumEuros(clientScoped.map((row) => row.amount));
  const offered = sumEuros(clientScoped.filter((row) => row.adjusted).map((row) => row.amount));
  const providerCost = sumEuros(clientScoped.map((row) => row.providerCost));
  const monthLabel = formatMonthLabel(month);
  const chart = useMemo(() => dailySeries(clientScoped, month), [clientScoped, month]);
  const chartTotals = chart.bars.map((bar) => bar.values.reduce((sum, value) => sum + value, 0));
  let busiest = { day: '', value: 0 };
  chart.categories.forEach((day, index) => {
    const value = chart.bars.reduce((sum, bar) => sum + (bar.values[index] ?? 0), 0);
    if (value > busiest.value) {
      busiest = { day, value };
    }
  });
  const chartSummary =
    total === 0
      ? `Aucune requête en ${monthLabel}.`
      : `Requêtes par jour en ${monthLabel} : ${formatInt(chartTotals[0] ?? 0)} réussies, ${formatInt(chartTotals[1] ?? 0)} échouées, ${formatInt(chartTotals[2] ?? 0)} rejetées${busiest.value > 0 ? ` ; journée la plus chargée le ${busiest.day} avec ${formatInt(busiest.value)} requêtes` : ''}.`;

  const loaded = requests.data !== undefined;
  const statValue = (value: string): string => (loaded ? value : '—');
  const empty = EMPTY_MESSAGES[filter];

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Requêtes' }]}
        actions={<MonthSwitcher month={month} onChange={setMonth} />}
      />
      <PageHead
        title="Requêtes"
        subtitle="Appels envoyés aux fournisseurs (IA, SMS). Montants HT."
      />

      <Row4 aria-label="Indicateurs des requêtes">
        <StatCard label="Requêtes" value={statValue(formatInt(total))} foot={monthLabel} />
        <StatCard
          label="Taux de réussite"
          value={statValue(total === 0 ? '—' : formatPercent((succeededCount / total) * 100))}
          foot={`${formatInt(failedCount)} échouée${failedCount > 1 ? 's' : ''}, ${formatInt(rejectedCount)} rejetée${rejectedCount > 1 ? 's' : ''}`}
        />
        <StatCard
          label="Facturé"
          value={statValue(formatEurCents(billed))}
          foot={`Offert : ${formatEurCents(offered)}`}
        />
        <StatCard
          label="Coût fournisseur"
          value={statValue(formatEurCents(providerCost))}
          foot="tokens et SMS"
        />
      </Row4>

      <Card>
        <CardHead
          title="Requêtes par jour"
          description={`${monthLabel.charAt(0).toUpperCase()}${monthLabel.slice(1)}, par issue`}
          actions={<ChartLegend items={CHART_LEGEND} />}
        />
        <CardContent className="grid gap-3">
          <TimeChart
            categories={chart.categories}
            bars={chart.bars}
            stacked
            ariaLabel={chartSummary}
            width={1100}
            height={220}
            padLeft={36}
            ticks={4}
            maxBar={36}
          />
        </CardContent>
      </Card>

      <Card>
        <CardBar
          start={
            <TableTabs
              aria-label="Filtrer par statut"
              value={filter}
              onValueChange={(next) => {
                setFilter(next);
                pagination.setPage(1);
              }}
              items={tabs}
            />
          }
          end={
            <>
              <NativeSelect
                controlSize="sm"
                wrapperClassName="w-48"
                aria-label="Filtrer par client"
                value={clientFilter}
                onChange={(event) => {
                  setClientFilter(event.target.value);
                  pagination.setPage(1);
                }}
              >
                <option value="all">Tous les clients</option>
                {clientOptions.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </NativeSelect>
              <SearchInput
                label="Rechercher une référence"
                placeholder="Rechercher une référence"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  pagination.setPage(1);
                }}
              />
            </>
          }
        />

        <Table className="min-w-[1000px]">
          <TableHeader>
            <TableRow>
              <TableHead kind="mono">Date</TableHead>
              <TableHead kind="name">Client</TableHead>
              <TableHead>Référence</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Cause</TableHead>
              <TableHead>Facturable</TableHead>
              <TableHead kind="num">Montant facturé HT</TableHead>
              <TableHead kind="num">Coût fournisseur</TableHead>
              <TableHead kind="act">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.isLoading ? (
              <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
            ) : requests.isError ? (
              <TableEmptyRow colSpan={COLUMN_COUNT}>
                Impossible de charger les requêtes.
              </TableEmptyRow>
            ) : rows.length === 0 ? (
              <TableEmptyRow colSpan={COLUMN_COUNT}>
                <b className="block font-semibold text-foreground">{empty.title}</b>
                <span>{empty.description}</span>
              </TableEmptyRow>
            ) : (
              pagination.pageItems.map((row) => {
                const ref = row.reference ?? row.id;
                const offerable = canOffer(row);
                return (
                  <TableRow
                    key={row.id}
                    onClick={() =>
                      void navigate({ to: '/requests/$requestId', params: { requestId: row.id } })
                    }
                  >
                    <TableCell kind="mono" className="whitespace-nowrap">
                      {formatDateTimeShort(row.createdAt)}
                    </TableCell>
                    <TableCell kind="name">{clientName(row.clientId)}</TableCell>
                    <TableCell>
                      <Link
                        to="/requests/$requestId"
                        params={{ requestId: row.id }}
                        className={rowLinkMonoClass}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        {ref}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={requestStatusLabel(row.status)} />
                    </TableCell>
                    <TableCell kind="sub" title={row.outcome ?? undefined}>
                      {row.outcome === null || row.outcome === 'succeeded'
                        ? '—'
                        : outcomeLabel(row.outcome)}
                    </TableCell>
                    <TableCell>{billableLabel(row)}</TableCell>
                    <TableCell kind="num">{formatEur(row.amount)}</TableCell>
                    <TableCell kind="num">{formatEur(row.providerCost)}</TableCell>
                    <TableCell
                      kind="act"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Actions pour la requête ${ref}`}
                          >
                            <Ellipsis aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() =>
                              void navigate({
                                to: '/requests/$requestId',
                                params: { requestId: row.id },
                              })
                            }
                          >
                            <FileSearch aria-hidden="true" />
                            Voir le détail
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={!offerable}
                            onSelect={() => {
                              setPick(row);
                            }}
                          >
                            <Gift aria-hidden="true" />
                            {row.adjusted ? 'Déjà offerte' : 'Offrir la requête…'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <Pager
          itemLabel="requêtes"
          {...pagination.pagerProps}
          pageSizeOptions={[10, 25, 50, 100]}
          showFirstLast
        />
      </Card>

      <ConfirmDialog
        open={pick !== null}
        onClose={() => {
          setPick(null);
        }}
        onConfirm={() => {
          if (pick !== null) {
            adjust.mutate(pick);
          }
        }}
        title="Offrir cette requête ?"
        subtitle={pick?.reference ?? pick?.id ?? ''}
        confirmLabel="Offrir (100 %)"
        busy={adjust.isPending}
        variant="destructive"
        note={
          'Le montant sera ramené à 0\u00a0€ et un ajustement de 100\u00a0% sera enregistré sur la facture du mois.'
        }
      />
    </>
  );
}
